import { createFileRoute } from "@tanstack/react-router";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Scale, Plus, Sparkles, Edit, Trash2 } from "lucide-react";

export const Route = createFileRoute("/governance/policies")({
  component: PoliciesPage,
});

const POLICIES = [
  { id: "pol-1", name: "Doctor: read patient within same tenant", effect: "ALLOW", target: "patient/*", actions: ["read"], conditions: "tenant_id = self.tenant_id AND role = 'DOCTOR'" },
  { id: "pol-2", name: "Nurse: read patient, no prescriptions", effect: "ALLOW", target: "patient/*", actions: ["read"], conditions: "tenant_id = self.tenant_id AND role = 'NURSE'" },
  { id: "pol-3", name: "Billing: read invoice, no clinical data", effect: "ALLOW", target: "invoice/*", actions: ["read", "create", "update"], conditions: "tenant_id = self.tenant_id AND role IN ('BILLING', 'ADMIN')" },
  { id: "pol-4", name: "Patient: read own data only", effect: "ALLOW", target: "patient/*", actions: ["read"], conditions: "patient.id = self.patient_id" },
  { id: "pol-5", name: "Audit: deny all writes to audit_events", effect: "DENY", target: "audit_event/*", actions: ["update", "delete"], conditions: "ALWAYS" },
  { id: "pol-6", name: "Doctor: sign prescription requires MFA", effect: "ALLOW", target: "prescription", actions: ["sign"], conditions: "role = 'DOCTOR' AND mfa_recent = true" },
];

function PoliciesPage() {
  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Scale className="h-5 w-5" /> Policies ABAC
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Règles d'accès basées sur les attributs (tenant, role, resource, action)</p>
      </div>

      <div className="rounded-lg border border-indigo-500/30 bg-indigo-500/5 p-3 flex items-start gap-2 text-sm">
        <Sparkles className="h-4 w-4 text-indigo-400 mt-0.5" />
        <div><strong>v7.0</strong> · éditeur visuel de policies avec simulation de décision. Pour l'instant, policies en lecture seule (basées sur <code>packages/abac</code>).</div>
      </div>

      <div className="flex items-center justify-end">
        <Button disabled>
          <Plus className="h-3.5 w-3.5 mr-1" /> Nouvelle policy (v7)
        </Button>
      </div>

      <div className="space-y-2">
        {POLICIES.map((p) => (
          <Card key={p.id}>
            <CardContent className="p-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <Badge variant={p.effect === "ALLOW" ? "default" : "destructive"} className="text-[10px]">
                    {p.effect}
                  </Badge>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{p.name}</div>
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">Cible:</span>
                    <code className="text-[11px] bg-white/5 px-1 rounded">{p.target}</code>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-muted-foreground">Actions:</span>
                    {p.actions.map((a) => (
                      <Badge key={a} variant="outline" className="text-[9px]">{a}</Badge>
                    ))}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    <span>Conditions: </span>
                    <code className="text-[11px] bg-white/5 px-1 rounded">{p.conditions}</code>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" disabled><Edit className="h-3 w-3" /></Button>
                  <Button size="sm" variant="ghost" disabled><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
